import { createApi } from '@reduxjs/toolkit/query/react';
import { Notistack } from '@/app/shared/components/Notistack';
import { getError } from '@/app/shared/utils/helpers';
import type { RootState } from '@/store';
import { baseQuery } from '@/store/api/baseQuery';
import { eventsApi } from '@/store/api/eventsApi';
import { setUILoading } from '@/store/reducers/uiSlice';
import { toggleErrorModal } from '@/store/reducers/uploadSlice';
export interface State {
  stateId: string;
  stateName: string;
}

export interface RelationshipType {
  relationshipTypeId: number;
  relationShipName: string;
}

export interface EmergencyContact {
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  relationshipTypeId: number;
  streetLine1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

const transformEmergencyContacts = (emergencyContacts: any[]): Record<string, Partial<EmergencyContact>> => {
  const mappedReferences: Record<string, Partial<EmergencyContact>> = {
    'Emergency Contact 1': {},
    'Emergency Contact 2': {},
    'Emergency Contact 3': {},
  };

  if (emergencyContacts.length)
    emergencyContacts.forEach((contact, index) => {
      const key = `Emergency Contact ${index + 1}`;
      if (mappedReferences[key]) {
        const { relationshipType, ...rest } = contact;
        mappedReferences[key] = {
          ...rest,
          ...(relationshipType && { relationshipTypeId: relationshipType }),
        };
      }
    });

  return mappedReferences;
};

export const referencesApi = createApi({
  reducerPath: 'referencesApi',
  tagTypes: ['EmergencyContactsReferences'],
  baseQuery,
  endpoints: (builder) => ({
    fetchEmergencyContactsReferences: builder.query<Record<string, Partial<EmergencyContact>>, string | null | undefined>({
      query: (contactId) => {
        if (!contactId) throw new Error('Missing contactId');
        return `/stips/contacts/${contactId}/references`;
      },
      transformResponse: (response: any[]) => transformEmergencyContacts(response),
      providesTags: ['EmergencyContactsReferences'],
      async onQueryStarted(contactId, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;
        
        const contactIdValue = contactId || state.auth.contactId;

        if (!contactIdValue) {
          dispatch(toggleErrorModal(true));
          return;
        }

        try {
          await queryFulfilled;

          dispatch(
            eventsApi.endpoints.addEventLog.initiate({
              event: 'Fetch emergency contacts successful',
            })
          );
        } catch (err: any) {
          console.error(err);
          Notistack.generalError(getError(err));
        }
      },
    }),
    fetchStates: builder.query<State[], void>({
      query: () => ({
        url: '/States',
        method: 'GET',
      }),
    }),
    fetchRelationshipTypes: builder.query<RelationshipType[], void>({
      query: () => ({
        url: '/RelationShipTypes',
        method: 'GET',
      }),
    }),
    createReference: builder.mutation<void, { contactId: string; referenceData: unknown }>({
      query: ({ contactId, referenceData }) => ({
        url: `/stips/contacts/${contactId}/contactreferences`,
        method: 'POST',
        body: referenceData,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(setUILoading(true));
        try {
          await queryFulfilled;
          dispatch(setUILoading(false));
        } catch {
          dispatch(setUILoading(false));
        }
      },
    }),
  }),
});

export const {
  useFetchStatesQuery,
  useFetchRelationshipTypesQuery,
  useCreateReferenceMutation,
  useFetchEmergencyContactsReferencesQuery,
} = referencesApi;
